import { useState, useEffect } from 'react';
import { getSubscriptionStatus, type SubscriptionStatus } from '@/services/subscriptionApi';
import { listAccounts } from '@/pages/AccountSettings/api';

export const usePaymentWarningBanner = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [canUseStripeServiceFees, setCanUseStripeServiceFees] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // First, get account data to check billing method
        const accounts = await listAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          const hasStripeBilling = account.service_fee_billing_method === 1;
          setCanUseStripeServiceFees(hasStripeBilling);

          // Only call subscription API if account uses Stripe billing
          if (hasStripeBilling) {
            try {
              const status = await getSubscriptionStatus();
              setSubscriptionStatus(status);
            } catch (error) {
              console.error('Failed to load subscription status:', error);
              // Even if API fails, we know from account data that Stripe is enabled
              setSubscriptionStatus({
                customer_id: undefined,
                has_payment_methods: false,
                subscriptions: [],
                message: 'Failed to load status',
              });
            }
          }
        } else {
          setCanUseStripeServiceFees(false);
        }
      } catch (error) {
        console.error('Failed to load account data:', error);
        setCanUseStripeServiceFees(false);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Show banner if:
  // 1. Not loading
  // 2. Account uses Stripe service fee billing (from database)
  // 3. Has subscription status data
  // 4. No payment methods on file
  const shouldShowBanner =
    !loading &&
    canUseStripeServiceFees &&
    subscriptionStatus &&
    !subscriptionStatus.has_payment_methods;

  return {
    shouldShowBanner,
    subscriptionStatus,
    loading,
    canUseStripeServiceFees,
  };
};
